import { API } from '@/config/api';
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminNavigation from '@/components/AdminNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { Calendar, RefreshCw, Bug } from 'lucide-react';

const COLORS = ['#0d9488', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

export default function ZooplanktonAnalytics() {
  const [searchParams] = useSearchParams();
  const [trends, setTrends] = useState([]);
  const [organisms, setOrganisms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [selectedOrganism, setSelectedOrganism] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState(() => searchParams.get('location') || 'all');
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedOrganism !== 'all') params.append('organism', selectedOrganism);
      if (selectedLocation !== 'all') params.append('location', selectedLocation);

      const res = await axios.get(`${API}/analytics/zooplankton-trends?${params}`, { withCredentials: true });
      const data = res.data;

      const processed = (data.trends || []).map(t => ({
        ...t,
        timestamp: new Date(t.date).getTime(),
        value: parseFloat(t.value) || 0
      })).filter(t => !isNaN(t.timestamp)).sort((a, b) => a.timestamp - b.timestamp);

      setTrends(processed);
      if (data.organisms?.length) setOrganisms(data.organisms);
      if (data.locations?.length) setLocations(data.locations);
      if (data.thresholds) setThresholds(data.thresholds);
    } catch (err) {
      console.error('Feil ved henting av zooplankton-data:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedOrganism, selectedLocation]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Get threshold for current organism
  const currentThreshold = selectedOrganism !== 'all' ? thresholds[selectedOrganism] : null;

  // Get color based on value + threshold
  const getColor = (val, species) => {
    const t = thresholds[species];
    if (!t) return '#10b981';
    if (t.threshold_red && val >= t.threshold_red) return '#ef4444';
    if (t.threshold_orange && val >= t.threshold_orange) return '#f97316';
    if (t.threshold_yellow && val >= t.threshold_yellow) return '#eab308';
    return '#10b981';
  };

  // Group by organism for multi-line chart when no specific organism selected
  const chartData = (() => {
    if (selectedOrganism !== 'all') return trends;

    // Group by date, pivot organisms as keys
    const byDate = {};
    trends.forEach(t => {
      if (!byDate[t.date]) byDate[t.date] = { date: t.date, timestamp: t.timestamp };
      // Sum values per organism per date
      byDate[t.date][t.organism] = (byDate[t.date][t.organism] || 0) + t.value;
    });
    return Object.values(byDate).sort((a, b) => a.timestamp - b.timestamp);
  })();

  // For multi-line: unique organisms in the data
  const activeOrganisms = selectedOrganism !== 'all' ? [] :
    [...new Set(trends.map(t => t.organism))].slice(0, 8);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <AdminNavigation />
      <main className="flex-1 min-h-screen p-4 lg:p-6 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Bug className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h1 data-testid="zooplankton-page-title" className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">Zooplankton-trender</h1>
                <p className="text-sm text-slate-600">Analyse av zooplanktondata over tid</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <Card className="border-slate-200 shadow-md mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Date presets */}
                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
                  {[
                    { label: 'I år', fn: () => { setStartDate(`${new Date().getFullYear()}-01-01`); setEndDate(new Date().toISOString().split('T')[0]); }},
                    { label: '6 mnd', fn: () => { const d = new Date(); d.setMonth(d.getMonth()-6); setStartDate(d.toISOString().split('T')[0]); setEndDate(new Date().toISOString().split('T')[0]); }},
                    { label: '1 år', fn: () => { const d = new Date(); d.setFullYear(d.getFullYear()-1); setStartDate(d.toISOString().split('T')[0]); setEndDate(new Date().toISOString().split('T')[0]); }},
                    { label: '2 år', fn: () => { const d = new Date(); d.setFullYear(d.getFullYear()-2); setStartDate(d.toISOString().split('T')[0]); setEndDate(new Date().toISOString().split('T')[0]); }},
                  ].map(p => (
                    <button
                      key={p.label}
                      onClick={p.fn}
                      className="px-3 py-1.5 text-sm rounded-md text-slate-600 hover:bg-slate-200 transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                {/* Date picker */}
                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent border-none text-sm text-slate-700 focus:outline-none w-[115px]" />
                  <span className="text-slate-400">–</span>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent border-none text-sm text-slate-700 focus:outline-none w-[115px]" />
                </div>

                {/* Organism filter */}
                <Select value={selectedOrganism} onValueChange={setSelectedOrganism}>
                  <SelectTrigger data-testid="zooplankton-organism-select" className="w-[200px] border-slate-300 bg-white shadow-sm">
                    <SelectValue placeholder="Velg organisme" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle organismar</SelectItem>
                    {organisms.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>

                {/* Location filter */}
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger data-testid="zooplankton-location-select" className="w-[180px] border-slate-300 bg-white shadow-sm">
                    <SelectValue placeholder="Velg stasjon" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle stasjonar</SelectItem>
                    {locations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>

                <Button variant="outline" size="icon" onClick={fetchData} className="h-9 w-9" data-testid="zooplankton-refresh-btn">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {/* Quick organism buttons */}
              {organisms.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {organisms.slice(0, 10).map(o => (
                    <button
                      key={o}
                      onClick={() => setSelectedOrganism(selectedOrganism === o ? 'all' : o)}
                      className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                        selectedOrganism === o
                          ? 'bg-purple-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Chart */}
          <Card className="border-slate-200 shadow-md mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-bold text-slate-900">
                {selectedOrganism !== 'all' ? selectedOrganism : 'Alle zooplankton'} — Utvikling
              </CardTitle>
              {currentThreshold && (
                <div className="flex flex-wrap items-center gap-4 mt-2 text-xs bg-slate-50 rounded-lg p-2.5">
                  <span className="text-slate-500 font-medium">Grenseverdiar:</span>
                  {currentThreshold.threshold_yellow && (
                    <div className="flex items-center gap-1">
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                      <span className="text-slate-600">Gul: {currentThreshold.threshold_yellow.toLocaleString('nb-NO')}</span>
                    </div>
                  )}
                  {currentThreshold.threshold_orange && (
                    <div className="flex items-center gap-1">
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-400" />
                      <span className="text-slate-600">Oransje: {currentThreshold.threshold_orange.toLocaleString('nb-NO')}</span>
                    </div>
                  )}
                  {currentThreshold.threshold_red && (
                    <div className="flex items-center gap-1">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      <span className="text-slate-600">Raud: {currentThreshold.threshold_red.toLocaleString('nb-NO')}</span>
                    </div>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-80">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-slate-600 text-sm">Laster data...</p>
                  </div>
                </div>
              ) : chartData.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-80 text-slate-500">
                  <Bug className="w-16 h-16 mb-4 text-slate-300" />
                  <p className="text-lg font-medium">Ingen zooplanktondata funne</p>
                  <p className="text-sm">Prøv å endre filter eller datoperiode</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="timestamp"
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      stroke="#94a3b8"
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickFormatter={v => { const d = new Date(v); return `${d.getDate()}/${d.getMonth()+1}`; }}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v.toLocaleString('nb-NO')}
                      label={{ value: 'Ant/L', angle: -90, position: 'insideLeft', style: { fill: '#64748b', fontSize: 12 } }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'white', border: 'none', borderRadius: '12px', padding: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                      labelFormatter={v => new Date(v).toLocaleDateString('nb-NO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      formatter={(value, name) => [`${value.toLocaleString('nb-NO')} ant/L`, name]}
                    />
                    <Legend />

                    {/* Threshold reference lines for single organism */}
                    {currentThreshold?.threshold_yellow && (
                      <ReferenceLine y={currentThreshold.threshold_yellow} stroke="#eab308" strokeDasharray="5 5" label={{ value: 'Gul', position: 'insideTopRight', fill: '#ca8a04', fontSize: 11 }} />
                    )}
                    {currentThreshold?.threshold_orange && (
                      <ReferenceLine y={currentThreshold.threshold_orange} stroke="#f97316" strokeDasharray="5 5" label={{ value: 'Oransje', position: 'insideTopRight', fill: '#ea580c', fontSize: 11 }} />
                    )}
                    {currentThreshold?.threshold_red && (
                      <ReferenceLine y={currentThreshold.threshold_red} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Raud', position: 'insideTopRight', fill: '#ef4444', fontSize: 11 }} />
                    )}

                    {selectedOrganism !== 'all' ? (
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#8b5cf6"
                        strokeWidth={2}
                        name={selectedOrganism}
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          if (!payload) return null;
                          const c = getColor(payload.value, selectedOrganism);
                          return <circle cx={cx} cy={cy} r={6} fill={c} stroke="#fff" strokeWidth={2} />;
                        }}
                        isAnimationActive={false}
                      />
                    ) : (
                      activeOrganisms.map((org, i) => (
                        <Line
                          key={org}
                          type="monotone"
                          dataKey={org}
                          stroke={COLORS[i % COLORS.length]}
                          strokeWidth={2}
                          name={org}
                          dot={{ r: 3 }}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                      ))
                    )}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Data table summary */}
          {!loading && trends.length > 0 && selectedOrganism !== 'all' && (
            <Card className="border-slate-200 shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-bold text-slate-900">Siste målingar</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="zooplankton-data-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-slate-500 font-medium">Dato</th>
                        <th className="text-left py-2 px-3 text-slate-500 font-medium">Stasjon</th>
                        <th className="text-left py-2 px-3 text-slate-500 font-medium">Område</th>
                        <th className="text-right py-2 px-3 text-slate-500 font-medium">Verdi (ant/L)</th>
                        <th className="text-center py-2 px-3 text-slate-500 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trends.slice(-20).reverse().map((t, i) => {
                        const color = getColor(t.value, t.organism);
                        const statusLabel = color === '#ef4444' ? 'Raud' : color === '#f97316' ? 'Oransje' : color === '#eab308' ? 'Gul' : 'Grøn';
                        return (
                          <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 px-3 text-slate-700">{t.date}</td>
                            <td className="py-2 px-3 text-slate-700">{t.location}</td>
                            <td className="py-2 px-3 text-slate-500">{t.area}</td>
                            <td className="py-2 px-3 text-right font-mono text-slate-800">{t.value.toLocaleString('nb-NO')}</td>
                            <td className="py-2 px-3 text-center">
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: color + '20', color }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                                {statusLabel}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
